import React, { useState } from 'react';
import Modal from '@components/common/Modal';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';

/**
 * Base modal component that provides common functionality for CRUD operations
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onSuccess - Function called on successful operation
 * @param {string} props.mode - Modal mode ('create', 'edit', 'delete', 'view')
 * @param {Object} props.item - Item being operated on
 * @param {string} props.itemType - Type of item for translations (e.g., 'resource', 'tag')
 * @param {Function} props.createFn - Function to create new item
 * @param {Function} props.updateFn - Function to update existing item
 * @param {Function} props.deleteFn - Function to delete item
 * @param {Function} props.FormComponent - Form component to render for create/edit
 * @param {Object} props.formProps - Additional props to pass to form component
 * @param {string} props.size - Modal size
 * @param {Object} props.translations - Translation keys override
 */
export const BaseModal = ({
    isOpen,
    onClose,
    onSuccess,
    mode = 'edit',
    item,
    itemType,
    createFn,
    updateFn,
    deleteFn,
    FormComponent,
    formProps = {},
    size = 'medium',
    translations = {}
}) => {
    const { t } = useLanguage();
    const notify = useNotification();
    const [loading, setLoading] = useState(false);

    const isCreating = mode === 'create';
    const isEditing = mode === 'edit' && !!item;
    const isDeleting = mode === 'delete';
    const isViewing = mode === 'view';

    // Default translation keys - handle plural forms for consistency
    const getAdminKey = (itemType) => {
        const pluralMap = {
            'resource': 'resources.admin',
            'category': 'categories.admin',
            'location': 'locations.admin',
            'tag': 'tags.admin',
            'consumption': 'consumptions.admin'
        };
        return pluralMap[itemType] || `${itemType}Admin`;
    };

    const adminKey = getAdminKey(itemType);
    const defaultTranslations = {
        createTitle: `${adminKey}.createTitle`,
        editTitle: `${adminKey}.editTitle`,
        deleteTitle: `${adminKey}.deleteTitle`,
        viewTitle: `${itemType}View.title`,
        deleteConfirmation: `${adminKey}.deleteConfirmation`,
        createSuccess: `${adminKey}.createSuccess`,
        updateSuccess: `${adminKey}.updateSuccess`,
        deleteSuccess: `${adminKey}.deleteSuccess`,
        createError: `${adminKey}.createError`,
        updateError: `${adminKey}.updateError`,
        deleteError: `${adminKey}.deleteError`,
        ...translations
    };

    const handleSubmit = async (formData) => {
        if (!createFn && !updateFn) {
            console.error('No create or update function provided');
            return;
        }

        setLoading(true);
        try {
            let response;
            if (isEditing && updateFn) {
                response = await updateFn(item.id, formData);
                notify.success(t(defaultTranslations.updateSuccess));
            } else if (isCreating && createFn) {
                response = await createFn(formData);
                notify.success(t(defaultTranslations.createSuccess));
            }

            onSuccess?.(response?.data);
            onClose();
            return response;
        } catch (err) {
            console.error(`Failed to ${isEditing ? 'update' : 'create'} ${itemType}:`, err);
            const errorKey = isEditing ? defaultTranslations.updateError : defaultTranslations.createError;
            notify.error(err.response?.data?.message || t(errorKey));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteFn) {
            console.error('No delete function provided');
            return;
        }

        setLoading(true);
        try {
            await deleteFn(item.id);
            notify.success(t(defaultTranslations.deleteSuccess));
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error(`Failed to delete ${itemType}:`, err);
            notify.error(t(defaultTranslations.deleteError));
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'create': return t(defaultTranslations.createTitle);
            case 'edit': return t(defaultTranslations.editTitle);
            case 'delete': return t(defaultTranslations.deleteTitle);
            case 'view': return t(defaultTranslations.viewTitle);
            default: return '';
        }
    };

    const getSize = () => {
        if (isDeleting) return 'small';
        return size;
    };

    if (isDeleting) {
        const label = item?.name || item?.title;
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={getTitle()}
                size={getSize()}
            >
                <div className="delete-confirmation">
                    <p>
                        {t(defaultTranslations.deleteConfirmation)}
                        {label && <strong>"{label}"</strong>}
                    </p>
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger"
                            disabled={loading}
                        >
                            {loading ? t('common.deleting') : t('common.delete')}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    if (isViewing) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={getTitle()}
                size={getSize()}
            >
                {FormComponent && (
                    <FormComponent
                        {...formProps}
                        item={item}
                        mode="view"
                        onCancel={onClose}
                        readOnly={true}
                    />
                )}
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={getTitle()}
            size={getSize()}
        >
            {FormComponent && (
                <FormComponent
                    {...formProps}
                    item={isEditing ? item : null}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    isEditing={isEditing}
                    loading={loading}
                />
            )}
        </Modal>
    );
};

export default BaseModal;
